import type { TokenOptimizationSummary } from '@ace-tracker/shared';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps } from '../chartTheme';
import { DeveloperName, useDeveloperProfiles } from '../settings';
import { ChartCard } from './ChartCard';
import { ExportMenu } from './ExportMenu';
import { EfficiencyGauge, type GaugeBand } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';
import {
  ChartEmptyState,
  compactNumber,
  ReadingGuide,
  wholeNumber,
  type SectionHeadingProps,
} from './OptimizationShared';
import type { SourceHealthInput } from '../health';
import { SourceEmptyState } from './SourceEmptyState';

/**
 * Graphify's modeled context reduction (1 − query context ÷ corpus). Graph
 * sub-queries are typically a small fraction of a whole corpus, so the bands
 * sit high: a graph that only halves context is a weak result.
 */
export const CONTEXT_REDUCTION_BANDS: GaugeBand[] = [
  { upTo: 50, label: 'Low' },
  { upTo: 75, label: 'Fair' },
  { upTo: 90, label: 'Moderate' },
  { upTo: 97, label: 'Good' },
  { upTo: 100, label: 'Excellent' },
];

/** Modeled share of corpus tokens a graph sub-query avoids; null when nothing was benchmarked. */
export function graphifyContextReductionPercent(averageCorpusTokens: number, averageQueryTokens: number): number | null {
  return averageCorpusTokens > 0 ? Math.max(0, (1 - averageQueryTokens / averageCorpusTokens) * 100) : null;
}

/**
 * The dedicated Graphify view: the measured cost of building graphs
 * (graphify-out/cost.json) kept visibly apart from the *modeled* benchmark
 * (estimated corpus vs. graph sub-query context for Graphify's sample
 * questions). Neither is observed assistant usage. All totals come from
 * buildTokenOptimizationSummary; this only lays them out.
 */
export function GraphifyDashboardBody({
  summary,
  SectionHeading,
  health,
}: {
  summary: TokenOptimizationSummary;
  SectionHeading: (props: SectionHeadingProps) => JSX.Element;
  /** Graphify's data health, to explain an empty view. */
  health?: SourceHealthInput;
}) {
  const { label: developerLabel } = useDeveloperProfiles();
  const totals = summary.graphifyTotals;
  const asOf = summary.graphifyAsOfDate;
  const coverage = summary.coverage;
  const coveragePercent = coverage.activeAiDevelopers > 0 ? (coverage.graphify / coverage.activeAiDevelopers) * 100 : null;

  if (!totals) {
    return <SourceEmptyState source="graphify" health={health} />;
  }

  const benchmarked = totals.benchmarkedRoots > 0;
  const contextReduction = graphifyContextReductionPercent(totals.averageCorpusTokens, totals.averageQueryTokens);
  const buildTokens = totals.buildInputTokens + totals.buildOutputTokens;
  const buildChart = summary.graphify
    .filter((row) => row.buildInputTokens + row.buildOutputTokens > 0)
    .map((row) => ({ label: developerLabel(row.developerId), input: row.buildInputTokens, output: row.buildOutputTokens }));
  const benchmarkChart = summary.graphify
    .filter((row) => row.benchmarkedRoots > 0)
    .map((row) => ({ label: developerLabel(row.developerId), corpus: row.averageCorpusTokens, query: row.averageQueryTokens }));

  return (
    <>
      <ReadingGuide>
        Graphify builds a knowledge graph of a repository so an assistant can pull a small graph sub-query instead of whole files.
        Build tokens are what Graphify itself reported spending to build the graphs — a measured cost. The benchmark compares an
        estimated whole-corpus token count with an estimated graph sub-query context for Graphify's sample questions — a model, not
        measured assistant usage or realized savings.
      </ReadingGuide>

      <section aria-labelledby="graphify-efficiency-heading">
        <div id="graphify-efficiency-heading">
          <SectionHeading title="Efficiency" hint={asOf ? `Latest snapshot per device · as of ${asOf}` : undefined} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Modeled context reduction"
            value={benchmarked ? contextReduction : null}
            bands={CONTEXT_REDUCTION_BANDS}
            meta={benchmarked ? `${totals.averageReductionRatio.toFixed(1)}× smaller context · ${wholeNumber(totals.benchmarkedRoots)} benchmarked roots` : 'No root met the benchmark threshold'}
            info="1 − (average estimated query context ÷ average estimated corpus tokens), across benchmarked roots. Graphify only benchmarks roots above its size threshold."
          />
          <EfficiencyGauge
            label="Graphify coverage"
            value={coveragePercent}
            valueLabel={coveragePercent === null ? undefined : `${Math.round(coveragePercent)}%`}
            meta={coverage.activeAiDevelopers > 0 ? `${coverage.graphify} of ${coverage.activeAiDevelopers} active AI developers` : 'No active AI developers in this range'}
            info="Active AI developers (at least one Copilot, Claude Code, or Codex request in the range) who also reported Graphify."
          />
        </div>
      </section>

      <section aria-labelledby="graphify-build-heading">
        <div id="graphify-build-heading">
          <SectionHeading title="Build cost" hint="Measured — from each root's graphify-out/cost.json" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard label="Tokens spent building graphs" value={compactNumber(buildTokens)} info="Lifetime model tokens Graphify reported for its extraction calls, summed across reporting roots." />
          <KpiCard label="Build input tokens" value={compactNumber(totals.buildInputTokens)} />
          <KpiCard label="Build output tokens" value={compactNumber(totals.buildOutputTokens)} />
          <KpiCard label="Builds" value={wholeNumber(totals.buildRuns)} meta={`across ${wholeNumber(totals.buildRoots)} roots`} />
        </div>
      </section>

      <section aria-labelledby="graphify-benchmark-heading">
        <div id="graphify-benchmark-heading">
          <SectionHeading title="Benchmark" hint="Modeled — Graphify's sample questions, averaged across benchmarked roots" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <KpiCard label="Estimated corpus" value={benchmarked ? compactNumber(totals.averageCorpusTokens) : '—'} unit={benchmarked ? 'tokens' : undefined} info="Average estimated tokens to read the whole corpus." />
          <KpiCard label="Estimated query context" value={benchmarked ? compactNumber(totals.averageQueryTokens) : '—'} unit={benchmarked ? 'tokens' : undefined} info="Average estimated tokens in a graph sub-query answer." />
          <KpiCard label="Estimated difference per query" value={benchmarked ? compactNumber(totals.averageEstimatedDeltaPerQuery) : '—'} unit={benchmarked ? 'tokens' : undefined} />
          <KpiCard label="Reduction ratio" value={benchmarked ? `${totals.averageReductionRatio.toFixed(1)}×` : '—'} info="Average corpus tokens ÷ average query context (ratio of means)." />
          <KpiCard
            label="Graph size"
            value={compactNumber(totals.totalNodes)}
            unit="nodes"
            meta={`${compactNumber(totals.totalEdges)} edges · ${wholeNumber(totals.devices)} devices · ${wholeNumber(totals.developers)} developers`}
            info="Nodes and edges across benchmarked graphs, summed over reporting devices. A repository on more than one machine is counted once per machine."
          />
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
          <ChartCard title="Build tokens by developer" caption="Measured input and output tokens Graphify spent building graphs." height="h-[320px]">
            {buildChart.length === 0 ? (
              <ChartEmptyState label={totals.buildRoots > 0 ? 'Graphify reported 0 build tokens for these roots.' : 'No graphify-out/cost.json records reported.'} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={buildChart} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke="var(--line-soft)" horizontal={false} />
                  <XAxis type="number" {...chartAxisTickProps} tickFormatter={(v: number) => compactNumber(Number(v))} />
                  <YAxis type="category" dataKey="label" {...chartAxisTickProps} width={120} />
                  <Tooltip {...chartTooltipProps} formatter={(value, name) => [wholeNumber(Number(value)), name]} />
                  <Legend {...chartLegendProps} />
                  <Bar dataKey="input" name="Input tokens" stackId="build" fill="#0058AB" />
                  <Bar dataKey="output" name="Output tokens" stackId="build" fill="#C77DFF" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          <ChartCard title="Corpus vs query context by developer" caption="Modeled benchmark averages for each developer's benchmarked roots." height="h-[320px]">
            {benchmarkChart.length === 0 ? (
              <ChartEmptyState label="No benchmarked roots in this range." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={benchmarkChart} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke="var(--line-soft)" horizontal={false} />
                  <XAxis type="number" {...chartAxisTickProps} tickFormatter={(v: number) => compactNumber(Number(v))} />
                  <YAxis type="category" dataKey="label" {...chartAxisTickProps} width={120} />
                  <Tooltip {...chartTooltipProps} formatter={(value, name) => [wholeNumber(Number(value)), name]} />
                  <Legend {...chartLegendProps} />
                  <Bar dataKey="corpus" name="Estimated corpus" fill="#99BCDD" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="query" name="Estimated query context" fill="#06D6A0" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </section>

      <section aria-labelledby="graphify-developers-heading">
        <div id="graphify-developers-heading">
          <SectionHeading title="By developer" hint="Build tokens summed; benchmark figures weighted by benchmarked roots" />
        </div>
        <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
          <ExportMenu title="Graphify by developer" className="-mr-2 -mt-2 mb-1 justify-end" />
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-3">Developer</th>
                  <th className="pb-2.5 pr-3 text-right">Build tokens</th>
                  <th className="pb-2.5 pr-3 text-right">Builds</th>
                  <th className="pb-2.5 pr-3 text-right">Benchmarked roots</th>
                  <th className="pb-2.5 pr-3 text-right">Corpus</th>
                  <th className="pb-2.5 pr-3 text-right">Query</th>
                  <th className="pb-2.5 pr-3 text-right">Context reduction</th>
                  <th className="pb-2.5 pr-3 text-right">Nodes / edges</th>
                  <th className="pb-2.5 text-right">Devices</th>
                </tr>
              </thead>
              <tbody>
                {summary.graphify.map((row) => {
                  const rowBenchmarked = row.benchmarkedRoots > 0;
                  const reduction = graphifyContextReductionPercent(row.averageCorpusTokens, row.averageQueryTokens);
                  return (
                    <tr key={row.developerId} className="border-b border-border last:border-0">
                      <td className="py-2.5 pr-3"><DeveloperName developerId={row.developerId} /></td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{wholeNumber(row.buildInputTokens + row.buildOutputTokens)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{wholeNumber(row.buildRuns)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{wholeNumber(row.benchmarkedRoots)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{rowBenchmarked ? wholeNumber(row.averageCorpusTokens) : '—'}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{rowBenchmarked ? wholeNumber(row.averageQueryTokens) : '—'}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">
                        {rowBenchmarked && reduction !== null ? `${reduction.toFixed(1)}% (${row.averageReductionRatio.toFixed(1)}×)` : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{rowBenchmarked ? `${compactNumber(row.totalNodes)} / ${compactNumber(row.totalEdges)}` : '—'}</td>
                      <td className="py-2.5 text-right tabular-nums">{row.devices}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
